import React from 'react';
import { spacing, components, typography, palette } from '../utils/designTokens';
import { LOADING_MESSAGE, EMPTY_DATA_MESSAGE } from '../utils/strings';
import { useTimeRange } from '../contexts/TimeRangeContext';

/**
 * PageLayout - Standard layout wrapper for list pages
 * Provides consistent spacing and structure for:
 * - Breadcrumb navigation (optional, above header like TraceDetail)
 * - Page header (title + display label)
 * - Stats bar with entity counts
 * - KPI pills section
 * - Filter pills section
 * - Main content area (table, cards, etc.)
 * - Pagination controls (optional, at bottom)
 * - Centralized loading/empty state handling
 *
 * Note: GlobalSelectors (Environment, App, TimeWindow) are now in the main Header component.
 */
const PageLayout = ({
  breadcrumb,         // Optional <Breadcrumb> component (rendered above header)
  title,              // Page title string (required if header not provided)
  header,             // <PageHeader> component (alternative to title)
  stats,              // <StatsBar> component with entity counts
  kpis,               // <KPIBar> component with KPI pills
  filters,            // <FilterPills> or filter components
  loading,            // Boolean: true when fetching data
  error,              // String or null: error message to display
  info,               // String or null: info message for boundary conditions (e.g., no data in time window)
  data,               // Array or null: null = not fetched yet, [] = fetched but empty, [...] = has data
  pagination,         // Optional: { page, limit, hasMore, onNext, onPrev, loading }
  children            // Main content (table, grid, etc.) - only rendered when data exists
}) => {
  const { displayLabel } = useTimeRange();
  return (
    <div className={`flex flex-col h-full ${spacing.md}`}>
      {/* Breadcrumb section (optional, above header) */}
      {breadcrumb && (
        <div className="flex-shrink-0">
          {breadcrumb}
        </div>
      )}

      {/* Header section */}
      <div className="flex-shrink-0 flex items-center gap-3">
        {header ? (
          header
        ) : title ? (
          <>
            <h1 className="text-xl font-bold text-text-primary">
              {title}
            </h1>
            {displayLabel && (
              <span className="text-[11px] text-text-muted">
                {displayLabel}
              </span>
            )}
          </>
        ) : null}
      </div>

      {/* KPI and Filter sections - hidden during loading. Stats moved to the footer
          (with pagination) so many filter pills no longer collide with the stats bar. */}
      {!loading && (kpis || filters) && (
        <div className="flex-shrink-0 flex items-center gap-2 flex-wrap">
          {kpis && <div className="flex-1">{kpis}</div>}
          {filters && <div className="flex-shrink-0">{filters}</div>}
        </div>
      )}

      {/* Main content area with centralized loading/error/info/empty state */}
      <div className="flex-1 min-h-0">
        {loading ? (
          <div className={components.table.empty}>
            {LOADING_MESSAGE}
          </div>
        ) : error ? (
          <div className={components.table.empty}>
            <div className="text-danger">{error}</div>
          </div>
        ) : info ? (
          <div className={components.table.empty}>
            <div className="text-text-muted">{info}</div>
            <div className="text-text-muted text-sm mt-2">Try selecting a longer time range</div>
          </div>
        ) : data === null ? (
          <div className={components.table.empty}>
            {LOADING_MESSAGE}
          </div>
        ) : data.length === 0 ? (
          <div className={components.table.empty}>
            {EMPTY_DATA_MESSAGE}
          </div>
        ) : (
          children
        )}
      </div>

      {/* Footer: stats (left) + pagination controls (right) */}
      {!loading && (stats || (data?.length > 0 && pagination)) && (
        <div className="flex-shrink-0 flex items-center justify-between gap-4 pt-4 px-2">
          {stats && <div className="flex-shrink-0">{stats}</div>}

          {data?.length > 0 && pagination && (
            <div className="flex items-center gap-3">
              <button
                onClick={pagination.onPrev}
                disabled={pagination.page === 1 || pagination.loading}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  pagination.page === 1 || pagination.loading
                    ? 'bg-background-tertiary text-text-muted cursor-not-allowed'
                    : 'bg-background-secondary text-text-primary hover:bg-background-tertiary'
                }`}
              >
                Previous
              </button>

              <div className="text-sm text-text-muted">
                Page {pagination.page} {pagination.hasMore ? '· More results available' : ''}
              </div>

              <button
                onClick={pagination.onNext}
                disabled={!pagination.hasMore || pagination.loading}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  !pagination.hasMore || pagination.loading
                    ? 'bg-background-tertiary text-text-muted cursor-not-allowed'
                    : 'bg-background-secondary text-text-primary hover:bg-background-tertiary'
                }`}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PageLayout;
